// C-01 search params (Frontend §6.1): validated from the URL with defaults stripped, so a fresh visit starts with the
// defaults and a shared link keeps its filters. Date presets stay as ids and are resolved in the clinic's time zone
// when the query runs (`toListParams`).
import type { ListAppointmentsParams } from '@pa/api/clinic'
import {
  addDays,
  APPOINTMENT_PRESETS,
  BOOKED_PRESETS,
  presetRange,
  rangeInstants,
  type DatePreset,
  type DateRangeValue,
  type LocalDate,
} from '@pa/ui'

export type View = 'booked' | 'appointment'
export type When = 'upcoming' | 'past' | 'all'

/** The Source filter's value for "Unknown" (no source): `noSource=true` on the API (API.md §6.2). */
export const UNKNOWN_SOURCE = '__unknown__'
/** The PF ID search box's length limit in the URL (Frontend §6.1): "PF-" plus a full GUID (39 characters) fits. */
export const Q_MAX = 40

export interface AppointmentsSearch {
  /** Default `booked` (stripped). */
  view?: 'appointment'
  /** Appointment view only; default `upcoming` (stripped). */
  when?: 'past' | 'all'
  /** A preset id of the view, or `custom` with `from` / `to` (clinic-local days, both inclusive). */
  range?: string
  from?: LocalDate
  to?: LocalDate
  /** A source slug, or `__unknown__`. */
  source?: string
  type?: string
  location?: string
  provider?: string
  q?: string
  /** Task 26 (D-83): "Only with conflicts" (`hasConflict=true` on #15); absent otherwise. */
  conflict?: true
  /** ≥ 2 (1 is stripped). */
  page?: number
  /** Opens C-01a. */
  appt?: string
  /** The stats card period; default `month` (stripped). */
  stats?: 'week' | '3m'
}

export type StatsPeriod = 'week' | 'month' | '3m'
/** The stats period; anything but `week` / `3m` is the default month (raw params can pass through the root route). */
export const statsOf = (s: AppointmentsSearch): StatsPeriod =>
  (s.stats as unknown) === 'week' || (s.stats as unknown) === '3m' ? (s.stats as StatsPeriod) : 'month'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DATE = /^\d{4}-\d{2}-\d{2}$/
const SLUG = /^[a-z0-9_-]{1,40}$/
/** API.md §6.2 `q`: an optional `pf-` / `pf` prefix, then `^[A-Za-z0-9-]{1,64}$`. */
const PF_QUERY = /^(?:pf-?)?([A-Za-z0-9-]{1,64})$/i

const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined)
const uuid = (v: unknown) => {
  const s = str(v)
  return s && UUID.test(s) ? s.toLowerCase() : undefined
}

export const viewOf = (s: AppointmentsSearch): View => s.view ?? 'booked'
export const whenOf = (s: AppointmentsSearch): When => s.when ?? 'upcoming'
export const presetsFor = (view: View): readonly DatePreset[] =>
  view === 'booked' ? BOOKED_PRESETS : APPOINTMENT_PRESETS

/** The URL's search params → a valid `AppointmentsSearch` (unknown or invalid values are dropped). */
export function parseSearch(raw: Record<string, unknown>): AppointmentsSearch {
  const out: AppointmentsSearch = {}
  if (raw.view === 'appointment') out.view = 'appointment'
  if (out.view && (raw.when === 'past' || raw.when === 'all')) out.when = raw.when
  const range = str(raw.range)
  const from = str(raw.from)
  const to = str(raw.to)
  if (range === 'custom') {
    if (from && to && DATE.test(from) && DATE.test(to) && from <= to) Object.assign(out, { range, from, to })
  } else if (range && range !== 'any' && presetsFor(viewOf(out)).some((p) => p.id === range)) {
    out.range = range
  }
  const source = str(raw.source)
  if (source && (source === UNKNOWN_SOURCE || SLUG.test(source))) out.source = source
  const type = uuid(raw.type)
  if (type) out.type = type
  const location = uuid(raw.location)
  if (location) out.location = location
  const provider = uuid(raw.provider)
  if (provider) out.provider = provider
  // The router parses `q=123` as a number.
  const q = typeof raw.q === 'number' ? String(raw.q) : str(raw.q)
  if (q) out.q = q.slice(0, Q_MAX)
  // The router parses `conflict=true` as a boolean.
  if (raw.conflict === true || raw.conflict === 'true') out.conflict = true
  const page = typeof raw.page === 'number' ? raw.page : Number(str(raw.page))
  if (Number.isInteger(page) && page > 1) out.page = page
  const appt = uuid(raw.appt)
  if (appt) out.appt = appt
  if (raw.stats === 'week' || raw.stats === '3m') out.stats = raw.stats
  return out
}

/** The date filter's value from the search. */
export const rangeValueOf = (s: AppointmentsSearch): DateRangeValue =>
  s.range === 'custom'
    ? { id: 'custom', ...(s.from && { from: s.from }), ...(s.to && { to: s.to }) }
    : { id: s.range ?? 'any' }

/** Any filter set (UserFlows C-01: "Clear filters" appears). The view itself isn't a filter. */
export function hasFilters(s: AppointmentsSearch): boolean {
  return (
    s.when !== undefined ||
    s.range !== undefined ||
    s.source !== undefined ||
    s.type !== undefined ||
    s.location !== undefined ||
    s.provider !== undefined ||
    s.conflict === true ||
    (s.q ?? '').trim() !== ''
  )
}

/** The search box text → the API's `q`, or `null` when it can't match any PF ID (no request needed). */
export function pfQuery(q: string | undefined): string | null | undefined {
  const t = (q ?? '').trim()
  if (t === '') return undefined
  return PF_QUERY.test(t) ? t : null
}

/** #15's query for the search, with presets resolved at the clinic's midnight (`to` exclusive, API.md §1.4). */
export function toListParams(s: AppointmentsSearch, timeZone: string, today: LocalDate): ListAppointmentsParams {
  const view = viewOf(s)
  const params: ListAppointmentsParams = { view }
  if (view === 'appointment') params.when = whenOf(s)
  const range =
    s.range === 'custom' && s.from && s.to
      ? { from: s.from, to: addDays(s.to, 1) }
      : s.range
        ? presetRange(s.range, today)
        : null
  if (range) Object.assign(params, rangeInstants(range, timeZone))
  if (s.source === UNKNOWN_SOURCE) params.noSource = 'true'
  else if (s.source) params.source = s.source
  if (s.type) params.visitTypeId = s.type
  if (s.location) params.locationId = s.location
  if (s.provider) params.providerId = s.provider
  if (s.conflict) params.hasConflict = 'true'
  const q = pfQuery(s.q)
  if (q) params.q = q
  if (s.page) params.page = String(s.page)
  return params
}
